// useGlobe.js - React hook wrapping GlobeEngine lifecycle

import { useEffect, useRef, useCallback } from 'react';
import { GlobeEngine } from './Globe';

export function useGlobe({ canvasRef, events, onEventClick, onHover, showConnections, allEvents }) {
  const engineRef = useRef(null);

  // Init engine on mount
  useEffect(() => {
    if (!canvasRef.current) return;
    const engine = new GlobeEngine(canvasRef.current);
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [canvasRef]);

  // Wire callbacks
  useEffect(() => {
    if (!engineRef.current) return;
    engineRef.current.onEventClick = onEventClick;
    engineRef.current.onHover = onHover;
  }, [onEventClick, onHover]);

  // Update events
  useEffect(() => {
    if (!engineRef.current || !events) return;
    engineRef.current.setEvents(events);
  }, [events]);

  // Update connections
  useEffect(() => {
    if (!engineRef.current || !events || !allEvents) return;
    if (showConnections) {
      engineRef.current.setConnections(events, allEvents);
    } else {
      engineRef.current._clearArcs();
    }
  }, [events, showConnections, allEvents]);

  const zoomToEvent = useCallback((event, callback) => {
    if (!engineRef.current) return;
    engineRef.current.zoomToEvent(event, callback);
  }, []);

  const resetView = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.resetView();
  }, []);

  const highlightEvent = useCallback((id) => {
    if (!engineRef.current) return;
    engineRef.current.highlightEvent(id);
  }, []);

  const clearHighlight = useCallback(() => {
    if (!engineRef.current) return;
    engineRef.current.clearHighlight();
  }, []);

  return { zoomToEvent, resetView, highlightEvent, clearHighlight, engineRef };
}
